import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api/axios";
import { getDraft, updateDraft } from "../api/invoices";
import { getAccountDeals, assignDealToAccount, removeDealAssignment, autoMatchAccount, AccountWithDeal, BitrixDeal } from "../api/bitrix";
import { listDraftAccounts, addAccountToDraft, removeAccountFromDraft, DraftAccountWithBitrix } from "../api/invoices";
import BitrixDealSearch from "./BitrixDealSearch";
import { ymdToDate } from "../utils/date";
import { colors, spacing, borderRadius, typography, shadows, createButtonStyle } from "../styles/theme";

/** ===== Typy pozycji ===== */
type PosBase = {
  type: "base";
  name?: string;
  amount_netto: number;
  vat: number;
  gtu_code?: string | null;
  description?: string;
};
type PosPercent = {
  type: "percent";
  name?: string;
  nickname: string;
  client_id?: string | null;
  source_type?: "allegro" | "google_ads";

  percent: number;
  vat: number;
  gtu_code?: string | null;
  threshold?: number;
  cap_enabled?: boolean;
  cap_amount?: number;
  description?: string;

  policy?: "rolling_30" | "previous_month";
  anchor_start?: string | null;
  month_key?: string | null;

  date_from?: string;
  date_to?: string;
};
type PosAdj = {
  type: "adjustment";
  name?: string;
  date_from: string;
  date_to: string;
  podstawa_do_wyrownania: number;
  vat: number;
  gtu_code?: string | null;
  description?: string;
};
type PosAdjPercent = {
  type: "adjustment_percent";
  name?: string;
  date_from: string;
  date_to: string;
  vat: number;
  gtu_code?: string | null;
  description?: string;
  source_type: "allegro" | "google_ads";
  client_id: string;
  client_numeric_id?: string;  // numeric DB id for Allegro (for missing-days)
  client_display_name?: string;  // descriptive name for Google Ads display
  percent: number;
  threshold?: number;
  cap_enabled?: boolean;
  cap_amount?: number;
};
type PosRevenuePercent = {
  type: "revenue_percent";
  name?: string;
  customer_id: string;
  descriptive_name: string;
  source_type?: "allegro" | "google_ads";
  percent: number;
  minimum: number;
  vat: number;
  gtu_code?: string | null;
  description?: string;

  policy?: "rolling_30" | "previous_month";
  anchor_start?: string | null;
  month_key?: string | null;

  date_from?: string;
  date_to?: string;
};
type PosBudget = {
  type: "budget";
  name?: string;
  customer_id: string;
  descriptive_name: string;
  source_type?: "allegro" | "google_ads";
  vat: number;
  gtu_code?: string | null;
  description?: string;

  policy?: "rolling_30" | "previous_month" | "custom";
  anchor_start?: string | null;
  month_key?: string | null;

  date_from?: string;
  date_to?: string;
};
type AnyPos = PosBase | PosPercent | PosAdj | PosAdjPercent | PosRevenuePercent | PosBudget;

type AllegroClient = { id: string; nickname: string };
type GoogleAdsClient = {
  id: number;
  customer_id: string;
  descriptive_name: string;
  mcc_label: string;
  mcc_email?: string;
  currency_code: string;
};
type Client = AllegroClient | GoogleAdsClient;

/** ===== Helpery daty – bez „uciekania” do UTC ===== */
const fmt = (d: Date) => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const monthKeyToRange = (mk: string) => {
  const [y, m] = mk.split("-").map(Number);
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  return { from: fmt(start), to: fmt(end) };
};
const previousMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0..11
  const start = new Date(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1, 1);
  const end = new Date(m === 0 ? y - 1 : y, (m === 0 ? 11 : m - 1) + 1, 0);
  return { from: fmt(start), to: fmt(end) };
};
const rollingRange = (anchor: string) => {
  const s = ymdToDate(anchor);
  const e = ymdToDate(anchor);
  e.setDate(e.getDate() + 30); // 31 dni łącznie
  return { from: fmt(s), to: fmt(e) };
};

export default function DraftEditor() {
  const params = useParams();
  const id = Number(params.id);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("Draft");
  const [invoiceType, setInvoiceType] = useState<"proforma" | "vat" | string>("proforma");
  const [departmentName, setDepartmentName] = useState<"semtop" | "adtarget">("semtop");  // NOWE

  const [positions, setPositions] = useState<AnyPos[]>([]);
  const isPercent = (p: AnyPos): p is PosPercent => p.type === "percent";
  const isRevenuePercent = (p: AnyPos): p is PosRevenuePercent => p.type === "revenue_percent";
  const isBudget = (p: AnyPos): p is PosBudget => p.type === "budget";

  /** Nick: dropdown live-search (2+ znaki), error per wiersz, busy per wiersz */
  const [nickErr, setNickErr] = useState<Record<number, string | null>>({});
  const [nickBusy, setNickBusy] = useState<Record<number, boolean>>({});
  const [nickOptions, setNickOptions] = useState<Record<number, Client[]>>({});
  const debounceRef = useRef<Record<number, number | undefined>>({});

  /** Missing days + toggle „Ukryj" */
  const [missing, setMissing] = useState<Record<number, string[]>>({});
  const [showMissing, setShowMissing] = useState<Record<number, boolean>>({});

  /** Bitrix deals section */
  const [accountDeals, setAccountDeals] = useState<AccountWithDeal[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealsError, setDealsError] = useState<string | null>(null);
  const [editingAccount, setEditingAccount] = useState<{ sourceType: "allegro" | "google_ads"; clientId: string } | null>(null);

  /** Manual account management */
  const [positionAccountKeys, setPositionAccountKeys] = useState<Set<string>>(new Set());
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccountType, setNewAccountType] = useState<"allegro" | "google_ads">("allegro");
  const [newAccountId, setNewAccountId] = useState("");
  const [newAccountDisplayName, setNewAccountDisplayName] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [addAccountError, setAddAccountError] = useState<string | null>(null);
  const [addAccountSearchQuery, setAddAccountSearchQuery] = useState("");
  const [addAccountOptions, setAddAccountOptions] = useState<Client[]>([]);
  const [addAccountSearchBusy, setAddAccountSearchBusy] = useState(false);
  const addAccountDebounceRef = useRef<number | undefined>(undefined);

  const hasNickErrors = useMemo(
    () => Object.values(nickErr).some(Boolean),
    [nickErr]
  );

  /** INIT */
  useEffect(() => {
    (async () => {
      try {
        const d = await getDraft(id);
        setName(d.name);
        setInvoiceType((d.payload?.type as any) || "proforma");
        setDepartmentName(d.department_name || "semtop");  // NOWE

        // Normalizacja pozycji (bez auto-przesuwania na froncie)
        const norm = (d.payload?.positions ?? []).map((p: AnyPos): AnyPos => {
          if (p.type === "percent") {
            const pp = { ...(p as PosPercent) };
            // zachowaj name i source_type jeśli przyszło w payloadzie
            pp.name = (p as any).name ?? pp.name;
            pp.source_type = (p as any).source_type ?? "allegro";

            if (pp.policy === "rolling_30" && pp.anchor_start) {
              const r = rollingRange(pp.anchor_start);
              pp.date_from = r.from;
              pp.date_to = r.to;
            } else {
              const r =
                pp.month_key && /^\d{4}-\d{2}$/.test(pp.month_key)
                  ? monthKeyToRange(pp.month_key)
                  : previousMonthRange();
              pp.date_from = r.from;
              pp.date_to = r.to;
              pp.policy = "previous_month";
            }
            if (pp.threshold == null) pp.threshold = 0;
            if (!pp.vat) pp.vat = 23;
            return pp;
          }

          if (p.type === "base") {
            const b = { ...(p as PosBase) };
            b.name = (p as any).name ?? b.name;
            return b;
          }

          if (p.type === "adjustment") {
            const a = { ...(p as PosAdj) };
            a.name = (p as any).name ?? a.name;
            return a;
          }

          if (p.type === "adjustment_percent") {
            const ap = { ...(p as PosAdjPercent) };
            ap.name = (p as any).name ?? ap.name;
            ap.source_type = (p as any).source_type ?? "allegro";
            ap.client_id = (p as any).client_id ?? "";
            ap.client_numeric_id = (p as any).client_numeric_id ?? undefined;
            ap.percent = (p as any).percent ?? 10;
            ap.threshold = (p as any).threshold ?? 1000;
            ap.cap_enabled = (p as any).cap_enabled ?? false;
            ap.cap_amount = (p as any).cap_amount ?? undefined;
            return ap;
          }

          if (p.type === "revenue_percent") {
            const rp = { ...(p as PosRevenuePercent) };
            rp.name = (p as any).name ?? rp.name;
            rp.source_type = (p as any).source_type ?? "google_ads";

            // Date logic (analogous to percent)
            if (rp.policy === "rolling_30" && rp.anchor_start) {
              const r = rollingRange(rp.anchor_start);
              rp.date_from = r.from;
              rp.date_to = r.to;
            } else {
              const r =
                rp.month_key && /^\d{4}-\d{2}$/.test(rp.month_key)
                  ? monthKeyToRange(rp.month_key)
                  : previousMonthRange();
              rp.date_from = r.from;
              rp.date_to = r.to;
              rp.policy = "previous_month";
            }
            if (!rp.vat) rp.vat = 23;
            return rp;
          }

          if (p.type === "budget") {
            const bp = { ...(p as PosBudget) };
            bp.name = (p as any).name ?? bp.name;
            bp.source_type = (p as any).source_type ?? "google_ads";

            // Date logic (analogous to revenue_percent + custom support)
            if (bp.policy === "rolling_30" && bp.anchor_start) {
              const r = rollingRange(bp.anchor_start);
              bp.date_from = r.from;
              bp.date_to = r.to;
            } else if (bp.policy === "custom") {
              // Keep existing date_from/date_to, no auto-advance
              bp.date_from = bp.date_from ?? "";
              bp.date_to = bp.date_to ?? "";
            } else {
              const r =
                bp.month_key && /^\d{4}-\d{2}$/.test(bp.month_key)
                  ? monthKeyToRange(bp.month_key)
                  : previousMonthRange();
              bp.date_from = r.from;
              bp.date_to = r.to;
              bp.policy = "previous_month";
            }
            if (!bp.vat) bp.vat = 23;
            return bp;
          }

          return p;
        });

        setPositions(norm);
      } catch (e: any) {
        setError(e?.response?.data?.detail || "Nie udało się wczytać draftu.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  /** Fetch Bitrix deal info for all accounts in draft (positions + extra) */
  const fetchBitrixDeals = async () => {
    // Extract unique (source_type, client_id) pairs from positions
    const accountKeys = new Set<string>();
    positions.forEach((p) => {
      if (p.type === "percent" && p.client_id) {
        accountKeys.add(`${p.source_type || "allegro"}:${p.client_id}`);
      }
      if (p.type === "adjustment_percent" && p.client_id) {
        accountKeys.add(`${p.source_type || "allegro"}:${p.client_id}`);
      }
      if (p.type === "revenue_percent" && p.customer_id) {
        accountKeys.add(`${p.source_type || "google_ads"}:${p.customer_id}`);
      }
      if (p.type === "budget" && p.customer_id) {
        accountKeys.add(`${p.source_type || "google_ads"}:${p.customer_id}`);
      }
    });

    setPositionAccountKeys(accountKeys);
    setLoadingDeals(true);
    setDealsError(null);

    try {
      // Fetch ALL accounts from draft (includes manually added + from positions)
      const draftAccounts = await listDraftAccounts(id).catch(() => [] as DraftAccountWithBitrix[]);

      // Convert to AccountWithDeal format for compatibility
      const allAccounts: AccountWithDeal[] = draftAccounts.map((acc) => ({
        source_type: acc.source_type as "allegro" | "google_ads",
        client_id: acc.client_id,
        display_name: acc.display_name,
        bitrix_deal: acc.bitrix_deal,
        is_manual_assignment: acc.is_manual_assignment,
      }));

      // If draft has no accounts yet, fetch from global account list and filter by positions
      if (allAccounts.length === 0 && accountKeys.size > 0) {
        const [allegroDeals, googleDeals] = await Promise.all([
          getAccountDeals("allegro").catch(() => [] as AccountWithDeal[]),
          getAccountDeals("google_ads").catch(() => [] as AccountWithDeal[]),
        ]);
        const allDeals = [...allegroDeals, ...googleDeals];
        const filtered = allDeals.filter((d) =>
          accountKeys.has(`${d.source_type}:${d.client_id}`) ||
          accountKeys.has(`${d.source_type}:${d.display_name}`)
        );
        setAccountDeals(filtered);
      } else {
        setAccountDeals(allAccounts);
      }

      // Trigger auto-match for accounts without deals (in background)
      const unmatchedAccounts = allAccounts.filter(
        (d) => !d.bitrix_deal && !d.is_manual_assignment
      );
      if (unmatchedAccounts.length > 0) {
        Promise.all(
          unmatchedAccounts.map((d) =>
            autoMatchAccount(d.source_type, d.client_id).catch(() => null)
          )
        ).then(() => {
          // Refresh after auto-matching
          listDraftAccounts(id).then((refreshed) => {
            const refreshedAccounts: AccountWithDeal[] = refreshed.map((acc) => ({
              source_type: acc.source_type as "allegro" | "google_ads",
              client_id: acc.client_id,
              display_name: acc.display_name,
              bitrix_deal: acc.bitrix_deal,
              is_manual_assignment: acc.is_manual_assignment,
            }));
            setAccountDeals(refreshedAccounts);
          });
        });
      }
    } catch (err) {
      setDealsError("Nie udalo sie pobrac informacji o dealach Bitrix");
    } finally {
      setLoadingDeals(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchBitrixDeals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, loading]);

  const handleAssignDeal = async (
    sourceType: "allegro" | "google_ads",
    clientId: string,
    deal: BitrixDeal
  ) => {
    try {
      await assignDealToAccount(sourceType, clientId, deal.id);
      // Refresh deals
      await fetchBitrixDeals();
      setEditingAccount(null);
    } catch (err) {
      setDealsError("Nie udalo sie przypisac deala");
    }
  };

  const handleClearDeal = async (
    sourceType: "allegro" | "google_ads",
    clientId: string
  ) => {
    try {
      await assignDealToAccount(sourceType, clientId, null);
      await fetchBitrixDeals();
      setEditingAccount(null);
    } catch (err) {
      setDealsError("Nie udalo sie usunac przypisania");
    }
  };

  /** Remove deal assignment and reset manual flag (allows auto-match again) */
  const handleRemoveDeal = async (
    sourceType: "allegro" | "google_ads",
    clientId: string
  ) => {
    try {
      await removeDealAssignment(sourceType, clientId);
      // Try auto-match after removing
      await autoMatchAccount(sourceType, clientId);
      await fetchBitrixDeals();
    } catch (err) {
      setDealsError("Nie udalo sie usunac przypisania");
    }
  };

  /** Check if account is from position (not deletable) or extra (deletable) */
  const isAccountFromPosition = (sourceType: string, clientId: string, displayName: string) => {
    return positionAccountKeys.has(`${sourceType}:${clientId}`) ||
           positionAccountKeys.has(`${sourceType}:${displayName}`);
  };

  /** Search accounts for add account dropdown */
  const handleAddAccountSearch = (query: string) => {
    setAddAccountSearchQuery(query);
    setAddAccountOptions([]);

    if (addAccountDebounceRef.current) {
      window.clearTimeout(addAccountDebounceRef.current);
    }

    if (query.trim().length < 2) return;

    addAccountDebounceRef.current = window.setTimeout(async () => {
      try {
        setAddAccountSearchBusy(true);
        let resp;
        if (newAccountType === "google_ads") {
          resp = await api.get<Client[]>("/google-ads/customers", {
            params: { search: query.trim() },
          });
        } else {
          resp = await api.get<Client[]>("/allegro-clients", {
            params: { nickname: query.trim() },
          });
        }
        setAddAccountOptions(Array.isArray(resp.data) ? resp.data : []);
      } catch {
        setAddAccountOptions([]);
      } finally {
        setAddAccountSearchBusy(false);
      }
    }, 250);
  };

  /** Select account from search dropdown */
  const handleSelectAddAccount = (c: Client) => {
    if (newAccountType === "google_ads") {
      const gc = c as GoogleAdsClient;
      setNewAccountId(gc.customer_id);
      setNewAccountDisplayName(gc.descriptive_name || gc.customer_id);
      setAddAccountSearchQuery(gc.descriptive_name || gc.customer_id);
    } else {
      const ac = c as AllegroClient;
      setNewAccountId(ac.id);
      setNewAccountDisplayName(ac.nickname);
      setAddAccountSearchQuery(ac.nickname);
    }
    setAddAccountOptions([]);
  };

  /** Add extra account to draft (for informational purposes) */
  const handleAddAccount = async () => {
    if (!newAccountId.trim()) {
      setAddAccountError("Wybierz konto z listy");
      return;
    }
    setAddingAccount(true);
    setAddAccountError(null);
    try {
      await addAccountToDraft(id, newAccountType, newAccountId.trim());
      setNewAccountId("");
      setNewAccountDisplayName("");
      setAddAccountSearchQuery("");
      setShowAddAccount(false);
      await fetchBitrixDeals();
    } catch (err: any) {
      setAddAccountError(err?.response?.data?.detail || "Nie udalo sie dodac konta");
    } finally {
      setAddingAccount(false);
    }
  };

  /** Remove extra account from draft */
  const handleRemoveAccount = async (sourceType: string, clientId: string) => {
    if (!window.confirm("Czy na pewno chcesz usunac to konto z draftu?")) return;
    try {
      await removeAccountFromDraft(id, sourceType, clientId);
      await fetchBitrixDeals();
    } catch (err) {
      setDealsError("Nie udalo sie usunac konta");
    }
  };

  /** Dodawanie / usuwanie */
  const addBase = () =>
    setPositions((p) => [
      ...p,
      { type: "base", name: "Podstawa", amount_netto: 0, vat: 23 } as PosBase,
    ]);

  const addPercent = () =>
    setPositions((p) => {
      const percent = 10;
      const threshold = 0;
      const defName = threshold === 0
        ? `${percent}% od całego budżetu`
        : `${percent}% od nadwyżki powyżej ${threshold} PLN`;
      return [
        ...p,
        {
          type: "percent",
          name: defName,
          nickname: "",
          client_id: null,
          source_type: "allegro",
          percent,
          vat: 23,
          threshold,
          policy: "previous_month",
          ...previousMonthRange(),
        } as PosPercent,
      ];
    });

  const addRevenuePercent = () =>
    setPositions((p) => {
      const percent = 5;
      const minimum = 899;
      const defName = `${percent}% od obrotu (min. ${minimum} PLN)`;
      return [
        ...p,
        {
          type: "revenue_percent",
          name: defName,
          customer_id: "",
          descriptive_name: "",
          source_type: "google_ads",
          percent,
          minimum,
          vat: 23,
          policy: "previous_month",
          ...previousMonthRange(),
        } as PosRevenuePercent,
      ];
    });

  const addBudget = () =>
    setPositions((p) => {
      const defName = "Budżet Google Ads";
      return [
        ...p,
        {
          type: "budget",
          name: defName,
          customer_id: "",
          descriptive_name: "",
          source_type: "google_ads",
          vat: 23,
          policy: "previous_month",
          ...previousMonthRange(),
        } as PosBudget,
      ];
    });

  const addAdj = () =>
    setPositions((p) => [
      ...p,
      {
        type: "adjustment",
        name: "Wyrównanie",
        date_from: "",
        date_to: "",
        podstawa_do_wyrownania: 0,
        vat: 23,
      } as PosAdj,
    ]);

  const addAdjPercent = () =>
    setPositions((p) => [
      ...p,
      {
        type: "adjustment_percent",
        name: "Wyrównanie %",
        date_from: "",
        date_to: "",
        vat: 23,
        source_type: "allegro",
        client_id: "",
        percent: 10,
        threshold: 1000,
        cap_enabled: false,
      } as PosAdjPercent,
    ]);

  const removeAt = (i: number) => {
    setPositions((p) => p.filter((_, idx) => idx !== i));
    setNickErr((e) => {
      const cp = { ...e }; delete cp[i]; return cp;
    });
    setNickOptions((o) => {
      const cp = { ...o }; delete cp[i]; return cp;
    });
    setMissing((m) => {
      const cp = { ...m }; delete cp[i]; return cp;
    });
    setShowMissing((v) => {
      const cp = { ...v }; delete cp[i]; return cp;
    });
  };

  /** Oblicz zakres dat dla pozycji z polityką dat */
  const computeRangeForPercent = (pp: PosPercent | PosRevenuePercent | PosBudget) => {
    if (pp.policy === "rolling_30" && pp.anchor_start) return rollingRange(pp.anchor_start);
    if (pp.policy === "previous_month") {
      if (pp.month_key && /^\d{4}-\d{2}$/.test(pp.month_key)) return monthKeyToRange(pp.month_key);
      return previousMonthRange();
    }
    if (pp.date_from && pp.date_to) return { from: pp.date_from, to: pp.date_to };
    return null;
  };

  /** Auto-sprawdzenie brakujących dni (po załadowaniu i każdej istotnej zmianie) */
  const silentCheckMissing = async (rowIndex: number, pp: PosPercent | PosRevenuePercent | PosBudget) => {
    const r = computeRangeForPercent(pp);

    // Determine ID field based on position type
    const clientId = pp.type === "percent"
      ? (pp as PosPercent).client_id
      : pp.type === "revenue_percent"
      ? (pp as PosRevenuePercent).customer_id
      : (pp as PosBudget).customer_id;

    if (!clientId || !r) {
      setMissing((prev) => ({ ...prev, [rowIndex]: [] }));
      return;
    }
    try {
      const sourceType = pp.source_type || (pp.type === "percent" ? "allegro" : "google_ads");
      let resp;

      if (sourceType === "google_ads") {
        resp = await api.get<string[] | { missing: string[] }>("/google-ads/missing-days", {
          params: { customer_id: clientId, date_from: r.from, date_to: r.to },
        });
      } else {
        resp = await api.get<string[] | { missing: string[] }>("/missing-days", {
          params: { client_id: clientId, date_from: r.from, date_to: r.to },
        });
      }

      const payload = resp.data as any;
      const list: string[] = Array.isArray(payload) ? payload : payload?.missing ?? [];
      setMissing((prev) => ({ ...prev, [rowIndex]: list }));
    } catch {
      setMissing((prev) => ({ ...prev, [rowIndex]: [] })); // nie blokujemy – traktujemy jak brak informacji
    }
  };

  /** Live-search (Allegro / Google Ads) (2+ znaki, dropdown jak w builderze) */
  const handleNickInput = (rowIndex: number, value: string) => {
    const pos = positions[rowIndex];
    if (!pos) return;

    // Update nickname or descriptive_name depending on type
    setPositions((arr) =>
      arr.map((it, idx) => {
        if (idx !== rowIndex) return it;
        if (it.type === "percent") {
          return { ...(it as PosPercent), nickname: value, client_id: null };
        } else if (it.type === "revenue_percent") {
          return { ...(it as PosRevenuePercent), descriptive_name: value, customer_id: "" };
        } else if (it.type === "budget") {
          return { ...(it as PosBudget), descriptive_name: value, customer_id: "" };
        }
        return it;
      })
    );
    setNickErr((prev) => ({ ...prev, [rowIndex]: null }));
    setNickOptions((prev) => ({ ...prev, [rowIndex]: [] }));

    if (debounceRef.current[rowIndex]) {
      window.clearTimeout(debounceRef.current[rowIndex]);
    }
    if ((value || "").trim().length < 2) return;

    if (pos.type !== "percent" && pos.type !== "revenue_percent" && pos.type !== "budget") return;
    const sourceType = pos.type === "percent"
      ? ((pos as PosPercent).source_type || "allegro")
      : pos.type === "revenue_percent"
      ? ((pos as PosRevenuePercent).source_type || "google_ads")
      : ((pos as PosBudget).source_type || "google_ads");

    debounceRef.current[rowIndex] = window.setTimeout(async () => {
      try {
        setNickBusy((b) => ({ ...b, [rowIndex]: true }));

        let resp;
        if (sourceType === "google_ads") {
          resp = await api.get<Client[]>("/google-ads/customers", {
            params: { search: value.trim() },
          });
        } else {
          resp = await api.get<Client[]>("/allegro-clients", {
            params: { nickname: value.trim() },
          });
        }

        const list = Array.isArray(resp.data) ? resp.data : [];
        setNickOptions((prev) => ({ ...prev, [rowIndex]: list }));
      } catch {
        setNickOptions((prev) => ({ ...prev, [rowIndex]: [] }));
      } finally {
        setNickBusy((b) => ({ ...b, [rowIndex]: false }));
      }
    }, 250);
  };

  const applyNickSuggestion = (rowIndex: number, c: Client) => {
    const pos = positions[rowIndex];
    if (!pos || pos.type !== "percent") return;
    const sourceType = (pos as PosPercent).source_type || "allegro";

    let nickname: string, client_id: string;
    if (sourceType === "google_ads") {
      const gc = c as GoogleAdsClient;
      nickname = gc.descriptive_name || gc.customer_id;
      client_id = gc.customer_id;
    } else {
      const ac = c as AllegroClient;
      nickname = ac.nickname;
      client_id = ac.id;
    }

    setPositions((arr) =>
      arr.map((it, idx) =>
        idx === rowIndex ? { ...(it as any), nickname, client_id } : it
      )
    );
    setNickErr((prev) => ({ ...prev, [rowIndex]: null }));
    setNickOptions((prev) => ({ ...prev, [rowIndex]: [] }));

    setTimeout(() => {
      const pp = positions[rowIndex] as PosPercent;
      silentCheckMissing(rowIndex, { ...pp, nickname, client_id });
    }, 0);
  };

  const blurNick = (rowIndex: number) => {
    const p = positions[rowIndex];
    if (!p) return;

    if (p.type === "percent") {
      const pp = p as PosPercent;
      const sourceType = pp.source_type || "allegro";
      const sourceName = sourceType === "google_ads" ? "Google Ads" : "Allegro";

      if (!pp.nickname?.trim()) {
        setNickErr((prev) => ({ ...prev, [rowIndex]: `Podaj nazwę klienta ${sourceName}.` }));
        return;
      }
      if (!pp.client_id) {
        setNickErr((prev) => ({ ...prev, [rowIndex]: "Wybierz klienta z listy sugerowanych." }));
      }
    } else if (p.type === "revenue_percent") {
      const rp = p as PosRevenuePercent;
      const sourceType = rp.source_type || "google_ads";
      const sourceName = sourceType === "allegro" ? "Allegro" : "Google Ads";

      if (!rp.descriptive_name?.trim()) {
        setNickErr((prev) => ({ ...prev, [rowIndex]: `Podaj nazwę klienta ${sourceName}.` }));
        return;
      }
      if (!rp.customer_id) {
        setNickErr((prev) => ({ ...prev, [rowIndex]: "Wybierz klienta z listy sugerowanych." }));
      }
    } else if (p.type === "budget") {
      const bp = p as PosBudget;
      const sourceType = bp.source_type || "google_ads";
      const sourceName = sourceType === "allegro" ? "Allegro" : "Google Ads";

      if (!bp.descriptive_name?.trim()) {
        setNickErr((prev) => ({ ...prev, [rowIndex]: `Podaj nazwę klienta ${sourceName}.` }));
        return;
      }
      if (!bp.customer_id) {
        setNickErr((prev) => ({ ...prev, [rowIndex]: "Wybierz klienta z listy sugerowanych." }));
      }
    }
  };

  /** Walidacje – bez blokady na missing */
  const validate = (): string | null => {
    if (hasNickErrors) return "Popraw błędne nazwy klientów.";
    for (const [i, p] of positions.entries()) {
      if (p.type === "percent") {
        const pp = p as PosPercent;
        const sourceType = pp.source_type || "allegro";
        const sourceName = sourceType === "google_ads" ? "Google Ads" : "Allegro";

        if (!pp.nickname?.trim()) return `Pozycja #${i + 1}: podaj nazwę klienta ${sourceName}.`;
        if (!pp.client_id) return `Pozycja #${i + 1}: wybierz klienta z listy.`;
        if (!(pp.percent >= 0)) return `Pozycja #${i + 1}: procent musi być ≥ 0.`;
        const th = pp.threshold ?? 0;
        if (!(th >= 0)) return `Pozycja #${i + 1}: próg (PLN) nie może być ujemny.`;
      }
      if (p.type === "revenue_percent") {
        const rp = p as PosRevenuePercent;
        const sourceType = rp.source_type || "google_ads";
        const sourceName = sourceType === "allegro" ? "Allegro" : "Google Ads";

        if (!rp.descriptive_name?.trim()) return `Pozycja #${i + 1}: podaj nazwę klienta ${sourceName}.`;
        if (!rp.customer_id) return `Pozycja #${i + 1}: wybierz klienta z listy.`;
        if (!(rp.percent >= 0)) return `Pozycja #${i + 1}: procent musi być ≥ 0.`;
        const min = rp.minimum ?? 0;
        if (!(min >= 0)) return `Pozycja #${i + 1}: minimum (PLN) nie może być ujemne.`;
      }
      if (p.type === "adjustment") {
        const adj = p as PosAdj;
        if (!adj.date_from || !adj.date_to) return `Pozycja #${i + 1}: podaj zakres dat.`;
        if (!(adj.podstawa_do_wyrownania && adj.podstawa_do_wyrownania > 0)) return `Pozycja #${i + 1}: podstawa musi być > 0.`;
        const df = ymdToDate(adj.date_from);
        const dt = ymdToDate(adj.date_to);
        if (df > dt) return `Pozycja #${i + 1}: data „Od" nie może być po „Do".`;
        if (df.getFullYear() !== dt.getFullYear() || df.getMonth() !== dt.getMonth()) {
          return `Pozycja #${i + 1}: daty muszą być w tym samym miesiącu.`;
        }
      }
      if (p.type === "adjustment_percent") {
        const ap = p as PosAdjPercent;
        if (!ap.date_from || !ap.date_to) return `Pozycja #${i + 1}: podaj zakres dat.`;
        if (!ap.client_id?.trim()) return `Pozycja #${i + 1}: wybierz klienta.`;
        if (!(ap.percent && ap.percent > 0)) return `Pozycja #${i + 1}: procent musi być > 0.`;
        const df = ymdToDate(ap.date_from);
        const dt = ymdToDate(ap.date_to);
        if (df > dt) return `Pozycja #${i + 1}: data „Od" nie może być po „Do".`;
      }
      if (p.type === "budget") {
        const bp = p as PosBudget;
        const sourceType = bp.source_type || "google_ads";
        const sourceName = sourceType === "allegro" ? "Allegro" : "Google Ads";

        if (!bp.descriptive_name?.trim()) return `Pozycja #${i + 1}: podaj nazwę klienta ${sourceName}.`;
        if (!bp.customer_id) return `Pozycja #${i + 1}: wybierz klienta z listy.`;
      }
    }
    return null;
  };

  /** Zmiany polityki i anchor — przelicz zakres + auto-check */
  /** previous_month: pełny poprzedni miesiąc, anchor nullem */
  const setPolicyPrev = (row: number) => {
    setPositions(arr =>
      arr.map((it, idx): AnyPos => {
        if (idx !== row) return it;
        const r = previousMonthRange();

        if (isPercent(it)) {
          const pp: PosPercent = {
            ...it,
            policy: "previous_month" as const,
            anchor_start: null,
            month_key: it.month_key ?? null,
            date_from: r.from,
            date_to: r.to,
          };
          return pp;
        }

        if (isRevenuePercent(it)) {
          const rp: PosRevenuePercent = {
            ...it,
            policy: "previous_month" as const,
            anchor_start: null,
            month_key: it.month_key ?? null,
            date_from: r.from,
            date_to: r.to,
          };
          return rp;
        }

        if (isBudget(it)) {
          const bp: PosBudget = {
            ...it,
            policy: "previous_month" as const,
            anchor_start: null,
            month_key: it.month_key ?? null,
            date_from: r.from,
            date_to: r.to,
          };
          return bp;
        }

        return it;
      })
    );
    setTimeout(() => {
      const pos = positions[row];
      if (isPercent(pos)) silentCheckMissing(row, pos);
      else if (isRevenuePercent(pos)) silentCheckMissing(row, pos);
    }, 0);
  };

  /** rolling_30: [anchor, anchor+30]; jeżeli nie podano anchor → sensowny start = koniec poprzedniego miesiąca */
  const setPolicyRolling = (row: number, anchor?: string) => {
    setPositions(arr =>
      arr.map((it, idx): AnyPos => {
        if (idx !== row) return it;

        if (isPercent(it)) {
          const start = anchor ?? it.anchor_start ?? previousMonthRange().to;
          const r = rollingRange(start);
          const pp: PosPercent = {
            ...it,
            policy: "rolling_30" as const,
            anchor_start: start,
            month_key: null,
            date_from: r.from,
            date_to: r.to,
          };
          return pp;
        }

        if (isRevenuePercent(it)) {
          const start = anchor ?? it.anchor_start ?? previousMonthRange().to;
          const r = rollingRange(start);
          const rp: PosRevenuePercent = {
            ...it,
            policy: "rolling_30" as const,
            anchor_start: start,
            month_key: null,
            date_from: r.from,
            date_to: r.to,
          };
          return rp;
        }

        if (isBudget(it)) {
          const start = anchor ?? it.anchor_start ?? previousMonthRange().to;
          const r = rollingRange(start);
          const bp: PosBudget = {
            ...it,
            policy: "rolling_30" as const,
            anchor_start: start,
            month_key: null,
            date_from: r.from,
            date_to: r.to,
          };
          return bp;
        }

        return it;
      })
    );
    setTimeout(() => {
      const pos = positions[row];
      if (isPercent(pos)) silentCheckMissing(row, pos);
      else if (isRevenuePercent(pos)) silentCheckMissing(row, pos);
    }, 0);
  };

  /** zmiana samego anchor = tak naprawdę rolling z nowym startem */
  const setAnchor = (row: number, v: string) => {
    setPolicyRolling(row, v);
  };

  /** Auto-check po pierwszym załadowaniu / zmianach nickname lub zakresu */
  useEffect(() => {
    positions.forEach((p, i) => {
      if (p.type === "percent") {
        const pp = p as PosPercent;
        if (pp.nickname && pp.client_id) silentCheckMissing(i, pp);
      } else if (p.type === "revenue_percent") {
        const rp = p as PosRevenuePercent;
        if (rp.descriptive_name && rp.customer_id) silentCheckMissing(i, rp);
      } else if (p.type === "budget") {
        const bp = p as PosBudget;
        if (bp.descriptive_name && bp.customer_id) silentCheckMissing(i, bp);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions.length]);

  /** Zapis */
  const save = async () => {
    setError(null);
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    // confirm jeśli są braki w dowolnym wierszu
    const withMissing = Object.entries(missing).filter(([_, days]) => (days?.length ?? 0) > 0);
    if (withMissing.length) {
      const lines = withMissing
        .map(([idx, days]) => `• Pozycja #${Number(idx) + 1}: ${days!.join(", ")}`)
        .join("\n");
      const ok = window.confirm(`W wybranych pozycjach brakuje danych/kosztów dla dni:\n${lines}\n\nCzy chcesz kontynuować mimo to?`);
      if (!ok) return;
    }

    try {
      const body: any = {
        name,
        department_name: departmentName,  // NOWE
        payload: { type: invoiceType, positions }
      };
      await updateDraft(id, body);
      alert("Zapisano draft.");
      window.history.back();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Nie udało się zapisać.");
    }
  };

  if (loading) {
    return (
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: spacing.xxl,
        textAlign: "center",
      }}>
        <div style={{
          fontSize: typography.fontSize.xl,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textSecondary,
        }}>
          ⏳ Ładowanie draftu...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 1200,
      margin: "0 auto",
      padding: spacing.xxl,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xxxl,
        fontWeight: typography.fontWeight.bold,
        color: colors.textPrimary,
        marginBottom: spacing.xl,
      }}>
        ✏️ Edytuj draft #{id}
      </h2>

      {error && (
        <div style={{
          padding: spacing.lg,
          marginBottom: spacing.xl,
          background: colors.errorLight,
          color: colors.error,
          border: `1px solid ${colors.error}`,
          borderRadius: borderRadius.md,
          fontWeight: typography.fontWeight.semibold,
          fontSize: typography.fontSize.base,
        }}>
          ⚠️ {error}
        </div>
      )}

      <div style={{
        display: "flex",
        gap: spacing.lg,
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: spacing.xl,
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: "flex", gap: spacing.sm, alignItems: "center", flex: "1 1 auto" }}>
          <label style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
          }}>
            Nazwa:
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              flex: "1 1 auto",
              minWidth: 200,
              padding: spacing.sm,
              fontSize: typography.fontSize.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              outline: "none",
            }}
            onFocus={(e) => { e.target.style.borderColor = colors.primary; }}
            onBlur={(e) => { e.target.style.borderColor = colors.border; }}
          />
        </div>

        <div style={{ display: "flex", gap: spacing.sm, alignItems: "center" }}>
          <label style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
          }}>
            Typ faktury:
          </label>
          <select
            value={invoiceType}
            onChange={(e) => setInvoiceType(e.target.value)}
            style={{
              padding: spacing.sm,
              fontSize: typography.fontSize.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="proforma">Proforma</option>
            <option value="vat">VAT</option>
          </select>
        </div>

        <div style={{ display: "flex", gap: spacing.sm, alignItems: "center" }}>
          <label style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
          }}>
            Firma:
          </label>
          <select
            value={departmentName}
            onChange={(e) => setDepartmentName(e.target.value as "semtop" | "adtarget")}
            style={{
              padding: spacing.sm,
              fontSize: typography.fontSize.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="semtop">Semtop</option>
            <option value="adtarget">AdTarget</option>
          </select>
        </div>
      </div>

      <div style={{
        display: "flex",
        gap: spacing.md,
        flexWrap: "wrap",
        marginBottom: spacing.xl,
      }}>
        <button onClick={addBase} style={createButtonStyle('secondary')}>
          + Podstawa
        </button>
        <button onClick={addPercent} style={createButtonStyle('secondary')}>
          + Procent
        </button>
        <button onClick={addAdj} style={createButtonStyle('secondary')}>
          + Wyrównanie
        </button>
        <button onClick={addAdjPercent} style={createButtonStyle('secondary')}>
          + Wyrównanie %
        </button>
        <button onClick={addRevenuePercent} style={createButtonStyle('secondary')}>
          + % od obrotu
        </button>
        <button onClick={addBudget} style={createButtonStyle('secondary')}>
          + Budżet
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          background: colors.backgroundAlt,
          borderRadius: borderRadius.md,
          overflow: "hidden",
        }}>
          <thead>
            <tr>
              <th style={th}>Typ</th>
              <th style={th}>Pola</th>
              <th style={{ ...th, width: 100, textAlign: "center" }}>Akcje</th>
            </tr>
          </thead>
        <tbody>
          {positions.map((p, i) => (
            <tr key={i} style={{
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = colors.gray50; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
              <td style={{
                ...td,
                fontWeight: typography.fontWeight.bold,
                color: colors.primary,
                fontSize: typography.fontSize.sm,
              }}>
                {p.type === "base" && "📌 Podstawa"}
                {p.type === "percent" && "📊 Procent"}
                {p.type === "adjustment" && "⚖️ Wyrównanie"}
                {p.type === "adjustment_percent" && "⚖️ Wyrównanie %"}
                {p.type === "revenue_percent" && "💰 % od obrotu"}
                {p.type === "budget" && "💳 Budżet"}
              </td>
              <td style={td}>
                {/* BASE */}
                {p.type === "base" && (
                  <div style={{
                    padding: spacing.lg,
                    background: colors.backgroundAlt,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.infoLight,
                      borderLeft: `3px solid ${colors.info}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Podstawa</strong>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Nazwa pozycji
                        </span>
                        <input
                          value={(p as PosBase).name ?? "Podstawa"}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosBase), name: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Netto (PLN)
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={(p as PosBase).amount_netto}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosBase), amount_netto: Number(e.target.value) } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          VAT (%)
                        </span>
                        <input
                          type="number"
                          step="1"
                          value={(p as PosBase).vat}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosBase), vat: Number(e.target.value) } : it))
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          GTU
                        </span>
                        <select
                          value={(p as PosBase).gtu_code || ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosBase), gtu_code: e.target.value || null } : it))
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        >
                          <option value="GTU_12">GTU_12</option>
                          <option value="">Brak</option>
                        </select>
                      </label>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                      <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                        Opis (opcjonalny)
                      </span>
                      <input
                        value={(p as PosBase).description ?? ""}
                        onChange={(e) =>
                          setPositions((arr) =>
                            arr.map((it, idx) =>
                              idx === i ? { ...(it as PosBase), description: e.target.value } : it
                            )
                          )
                        }
                        style={{
                          padding: spacing.sm,
                          fontSize: typography.fontSize.sm,
                          border: `1px solid ${colors.border}`,
                          borderRadius: borderRadius.sm,
                        }}
                      />
                    </label>
                  </div>
                )}

                {/* PERCENT */}
                {p.type === "percent" && (
                  <div style={{
                    padding: spacing.lg,
                    background: colors.backgroundAlt,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                  }}>
                    {/* Banner */}
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.primaryLight,
                      borderLeft: `3px solid ${colors.primary}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Podstawa + Procent</strong>
                      <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Kwota = podstawa + (koszt - próg) * procent / 100
                      </div>
                    </div>

                    {/* Source Selector */}
                    <div style={{ marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Źródło danych:
                        </span>
                        <select
                          value={(p as PosPercent).source_type || "allegro"}
                          onChange={(e) => {
                            const newSourceType = e.target.value as "allegro" | "google_ads";
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosPercent), source_type: newSourceType, nickname: "", client_id: null }
                                  : it
                              )
                            );
                            setNickErr((prev) => ({ ...prev, [i]: null }));
                            setNickOptions((prev) => ({ ...prev, [i]: [] }));
                            setMissing((prev) => ({ ...prev, [i]: [] }));
                          }}
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                            background: colors.background,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="allegro">Allegro Ads</option>
                          <option value="google_ads">Google Ads</option>
                        </select>
                      </label>
                    </div>

                    {/* Client Search */}
                    <div style={{ position: "relative", marginBottom: spacing.lg }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Klient {(p as PosPercent).source_type === "google_ads" ? "Google Ads" : "Allegro"}:
                        </span>
                        <input
                          value={(p as PosPercent).nickname}
                          onChange={(e) => handleNickInput(i, e.target.value)}
                          onBlur={() => blurNick(i)}
                          placeholder="min. 2 znaki"
                          autoComplete="off"
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                            width: "100%",
                          }}
                        />
                      </label>
                      {!!(nickOptions[i]?.length ?? 0) && (
                        <ul style={{
                          position: "absolute", top: "100%", left: 0,
                          background: colors.background,
                          border: `1px solid ${colors.border}`,
                          zIndex: 1000,
                          listStyle: "none",
                          margin: `${spacing.xs}px 0 0 0`,
                          padding: 0,
                          width: "100%",
                          maxHeight: 180,
                          overflowY: "auto",
                          boxShadow: shadows.lg,
                          borderRadius: borderRadius.sm
                        }}>
                          {nickOptions[i]!.map((c) => {
                            const sourceType = (p as PosPercent).source_type || "allegro";
                            const isAllegro = sourceType === "allegro";
                            const displayText = isAllegro
                              ? `${(c as AllegroClient).nickname} (${(c as AllegroClient).id})`
                              : `${(c as GoogleAdsClient).descriptive_name || (c as GoogleAdsClient).customer_id} (${(c as GoogleAdsClient).customer_id})`;
                            const key = isAllegro ? (c as AllegroClient).id : (c as GoogleAdsClient).customer_id;
                            const mccEmail = !isAllegro && (c as GoogleAdsClient).mcc_email ? (c as GoogleAdsClient).mcc_email : null;

                            return (
                              <li
                                key={key}
                                style={{
                                  padding: spacing.md,
                                  cursor: "pointer",
                                  borderBottom: `1px solid ${colors.borderLight}`,
                                  transition: "background 0.2s ease"
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => applyNickSuggestion(i, c)}
                              >
                                {displayText}
                                {mccEmail && <span style={{ color: colors.textSecondary, marginLeft: spacing.sm }}>[MCC: {mccEmail}]</span>}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {nickBusy[i] && <span style={{ marginTop: spacing.xs, fontSize: typography.fontSize.sm, color: colors.textSecondary }}>⏳ Wyszukiwanie...</span>}
                      {nickErr[i] && (
                        <div style={{
                          marginTop: spacing.xs,
                          padding: spacing.sm,
                          background: colors.errorLight,
                          color: colors.errorDark,
                          borderRadius: borderRadius.sm,
                          fontSize: typography.fontSize.sm
                        }}>
                          {nickErr[i]}
                        </div>
                      )}
                    </div>

                    {/* Radio buttons for date mode */}
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      display: "flex",
                      flexDirection: "column",
                      gap: spacing.sm
                    }}>
                      <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                        Tryb daty:
                      </span>
                      <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`policy_${i}`}
                          checked={(p as PosPercent).policy === "rolling_30"}
                          onChange={() => setPolicyRolling(i)}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>
                          Umowy miesięczne (data od +30 dni, 31 dni łącznie)
                        </span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`policy_${i}`}
                          checked={(p as PosPercent).policy !== "rolling_30"}
                          onChange={() => setPolicyPrev(i)}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>
                          Nieokreślony (ostatni pełny miesiąc)
                        </span>
                      </label>

                      {(p as PosPercent).policy === "rolling_30" && (
                        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, marginTop: spacing.sm }}>
                          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                            Data od:
                          </span>
                          <input
                            type="date"
                            value={(p as PosPercent).anchor_start ?? ""}
                            onChange={(e) => setAnchor(i, e.target.value)}
                            style={{
                              padding: `${spacing.sm}px ${spacing.md}px`,
                              border: `1px solid ${colors.border}`,
                              borderRadius: borderRadius.sm,
                              fontSize: typography.fontSize.base,
                            }}
                          />
                        </label>
                      )}
                    </div>

                    {/* Grid layouts for fields */}
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Nazwa pozycji:
                        </span>
                        <input
                          value={(p as PosPercent).name ?? ""}
                          placeholder={
                            ((p as PosPercent).threshold ?? 0) === 0
                              ? `${(p as PosPercent).percent}% od całego budżetu`
                              : `${(p as PosPercent).percent}% od nadwyżki powyżej ${(p as PosPercent).threshold ?? 0} PLN`
                          }
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosPercent), name: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Procent (%):
                        </span>
                        <input
                          type="number"
                          step="0.1"
                          value={(p as PosPercent).percent}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosPercent), percent: Number(e.target.value) } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: spacing.md, marginBottom: spacing.lg }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          VAT (%):
                        </span>
                        <input
                          type="number"
                          step="1"
                          value={(p as PosPercent).vat}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosPercent), vat: Number(e.target.value) } : it))
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          GTU:
                        </span>
                        <select
                          value={(p as PosPercent).gtu_code || ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosPercent), gtu_code: e.target.value || null } : it))
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        >
                          <option value="GTU_12">GTU_12</option>
                          <option value="">Brak</option>
                        </select>
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Próg (PLN):
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={(p as PosPercent).threshold ?? 0}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosPercent), threshold: Number(e.target.value) } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Opis (opcjonalny):
                        </span>
                        <input
                          value={(p as PosPercent).description ?? ""}
                          placeholder="Tylko wewnętrznie"
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosPercent), description: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>
                    </div>

                    {/* Cap (limit nadwyżki) */}
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.border}`
                    }}>
                      <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer", marginBottom: (p as PosPercent).cap_enabled ? spacing.md : 0 }}>
                        <input
                          type="checkbox"
                          checked={(p as PosPercent).cap_enabled ?? false}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosPercent), cap_enabled: e.target.checked } : it
                              )
                            )
                          }
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Blokada nadwyżki (cap)
                        </span>
                      </label>
                      {(p as PosPercent).cap_enabled && (
                        <div style={{ marginTop: spacing.sm }}>
                          <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                              Maksymalna kwota netto (PLN):
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min={0}
                              value={(p as PosPercent).cap_amount ?? 0}
                              onChange={(e) =>
                                setPositions((arr) =>
                                  arr.map((it, idx) =>
                                    idx === i ? { ...(it as PosPercent), cap_amount: Number(e.target.value) } : it
                                  )
                                )
                              }
                              placeholder="Wpisz maksymalną kwotę"
                              style={{
                                padding: `${spacing.sm}px ${spacing.md}px`,
                                border: `1px solid ${colors.border}`,
                                borderRadius: borderRadius.sm,
                                fontSize: typography.fontSize.base,
                              }}
                            />
                          </label>
                          <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                            Jeśli obliczona kwota przekroczy limit, zostanie obcięta do tej wartości.
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Period display with missing days check */}
                    <div style={{
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      marginBottom: spacing.md,
                    }}>
                      <div style={{ fontSize: typography.fontSize.sm, marginBottom: spacing.sm }}>
                        <strong>Okres:</strong> {(p as PosPercent).date_from} — {(p as PosPercent).date_to}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const shown = !!showMissing[i];
                          if (!shown) silentCheckMissing(i, p as PosPercent);
                          setShowMissing((prev) => ({ ...prev, [i]: !shown }));
                        }}
                        style={{
                          ...createButtonStyle('secondary'),
                          padding: `${spacing.xs}px ${spacing.md}px`,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {showMissing[i] ? "Ukryj brakujące dni" : "Sprawdź brakujące dni"}
                      </button>
                      {showMissing[i] && (missing[i]?.length ?? 0) > 0 && (
                        <div style={{
                          marginTop: spacing.sm,
                          padding: spacing.md,
                          background: colors.warningLight,
                          border: `1px solid ${colors.warning}`,
                          borderRadius: borderRadius.sm,
                          color: colors.warningDark,
                          fontSize: typography.fontSize.sm
                        }}>
                          <strong>⚠️ Brak danych/kosztów:</strong> {missing[i].join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* REVENUE_PERCENT */}
                {p.type === "revenue_percent" && (
                  <div style={{
                    padding: spacing.lg,
                    background: colors.backgroundAlt,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                  }}>
                    {/* Banner */}
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.infoLight,
                      borderLeft: `3px solid ${colors.info}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: % od obrotu</strong>
                      <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Oblicza kwotę jako MAX(obrót * percent / 100, minimum)
                      </div>
                    </div>

                    {/* Source Selector */}
                    <div style={{ marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Źródło danych:
                        </span>
                        <select
                          value={(p as PosRevenuePercent).source_type || "google_ads"}
                          onChange={(e) => {
                            const newSourceType = e.target.value as "allegro" | "google_ads";
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosRevenuePercent), source_type: newSourceType, customer_id: "", descriptive_name: "" }
                                  : it
                              )
                            );
                            setNickErr((prev) => ({ ...prev, [i]: null }));
                            setNickOptions((prev) => ({ ...prev, [i]: [] }));
                            setMissing((prev) => ({ ...prev, [i]: [] }));
                          }}
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                            background: colors.background,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="allegro">Allegro Ads</option>
                          <option value="google_ads">Google Ads</option>
                        </select>
                      </label>
                    </div>

                    {/* Client Search */}
                    <div style={{ position: "relative", marginBottom: spacing.lg }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Klient {(p as PosRevenuePercent).source_type === "allegro" ? "Allegro" : "Google Ads"}:
                        </span>
                        <input
                          value={(p as PosRevenuePercent).descriptive_name}
                          onChange={(e) => handleNickInput(i, e.target.value)}
                          onBlur={() => blurNick(i)}
                          placeholder="min. 2 znaki"
                          autoComplete="off"
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                            width: "100%",
                          }}
                        />
                      </label>
                      {!!(nickOptions[i]?.length ?? 0) && (
                        <ul style={{
                          position: "absolute", top: "100%", left: 0,
                          background: colors.background,
                          border: `1px solid ${colors.border}`,
                          zIndex: 1000,
                          listStyle: "none",
                          margin: `${spacing.xs}px 0 0 0`,
                          padding: 0,
                          width: "100%",
                          maxHeight: 180,
                          overflowY: "auto",
                          boxShadow: shadows.lg,
                          borderRadius: borderRadius.sm
                        }}>
                          {nickOptions[i]!.map((c) => {
                            const sourceType = (p as PosRevenuePercent).source_type || "google_ads";
                            const isAllegro = sourceType === "allegro";
                            const displayText = isAllegro
                              ? `${(c as AllegroClient).nickname} (${(c as AllegroClient).id})`
                              : `${(c as GoogleAdsClient).descriptive_name || (c as GoogleAdsClient).customer_id} (${(c as GoogleAdsClient).customer_id})`;
                            const key = isAllegro ? (c as AllegroClient).id : (c as GoogleAdsClient).customer_id;
                            const mccEmail = !isAllegro && (c as GoogleAdsClient).mcc_email ? (c as GoogleAdsClient).mcc_email : null;

                            return (
                              <li
                                key={key}
                                style={{
                                  padding: spacing.md,
                                  cursor: "pointer",
                                  borderBottom: `1px solid ${colors.borderLight}`,
                                  transition: "background 0.2s ease"
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  let customer_id: string, descriptive_name: string;
                                  if (isAllegro) {
                                    const ac = c as AllegroClient;
                                    customer_id = ac.id;
                                    descriptive_name = ac.nickname;
                                  } else {
                                    const gc = c as GoogleAdsClient;
                                    customer_id = gc.customer_id;
                                    descriptive_name = gc.descriptive_name || gc.customer_id;
                                  }
                                  setPositions((arr) =>
                                    arr.map((it, idx) =>
                                      idx === i ? { ...(it as any), customer_id, descriptive_name } : it
                                    )
                                  );
                                  setNickErr((prev) => ({ ...prev, [i]: null }));
                                  setNickOptions((prev) => ({ ...prev, [i]: [] }));
                                }}
                              >
                                {displayText}
                                {mccEmail && <span style={{ color: colors.textSecondary, marginLeft: spacing.sm }}>[MCC: {mccEmail}]</span>}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {nickBusy[i] && <span style={{ marginTop: spacing.xs, fontSize: typography.fontSize.sm, color: colors.textSecondary }}>⏳ Wyszukiwanie...</span>}
                      {nickErr[i] && (
                        <div style={{
                          marginTop: spacing.xs,
                          padding: spacing.sm,
                          background: colors.errorLight,
                          color: colors.errorDark,
                          borderRadius: borderRadius.sm,
                          fontSize: typography.fontSize.sm
                        }}>
                          {nickErr[i]}
                        </div>
                      )}
                    </div>

                    {/* Radio buttons for date mode */}
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      display: "flex",
                      flexDirection: "column",
                      gap: spacing.sm
                    }}>
                      <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                        Tryb daty:
                      </span>
                      <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`policy_rp_${i}`}
                          checked={(p as PosRevenuePercent).policy === "rolling_30"}
                          onChange={() => setPolicyRolling(i)}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>
                          Umowy miesięczne (data od +30 dni, 31 dni łącznie)
                        </span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`policy_rp_${i}`}
                          checked={(p as PosRevenuePercent).policy !== "rolling_30"}
                          onChange={() => setPolicyPrev(i)}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>
                          Nieokreślony (ostatni pełny miesiąc)
                        </span>
                      </label>

                      {(p as PosRevenuePercent).policy === "rolling_30" && (
                        <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, marginTop: spacing.sm }}>
                          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                            Data od:
                          </span>
                          <input
                            type="date"
                            value={(p as PosRevenuePercent).anchor_start ?? ""}
                            onChange={(e) => setAnchor(i, e.target.value)}
                            style={{
                              padding: `${spacing.sm}px ${spacing.md}px`,
                              border: `1px solid ${colors.border}`,
                              borderRadius: borderRadius.sm,
                              fontSize: typography.fontSize.base,
                            }}
                          />
                        </label>
                      )}
                    </div>

                    {/* Grid layouts for fields */}
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Nazwa pozycji:
                        </span>
                        <input
                          value={(p as PosRevenuePercent).name ?? ""}
                          placeholder={`${(p as PosRevenuePercent).percent}% od obrotu (min. ${(p as PosRevenuePercent).minimum ?? 0} PLN)`}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosRevenuePercent), name: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Procent (%):
                        </span>
                        <input
                          type="number"
                          step="0.1"
                          value={(p as PosRevenuePercent).percent}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosRevenuePercent), percent: Number(e.target.value) } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Minimum (PLN):
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={(p as PosRevenuePercent).minimum ?? 0}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosRevenuePercent), minimum: Number(e.target.value) } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          VAT (%):
                        </span>
                        <input
                          type="number"
                          step="1"
                          value={(p as PosRevenuePercent).vat}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosRevenuePercent), vat: Number(e.target.value) } : it))
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          GTU:
                        </span>
                        <select
                          value={(p as PosRevenuePercent).gtu_code || ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosRevenuePercent), gtu_code: e.target.value || null } : it))
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        >
                          <option value="GTU_12">GTU_12</option>
                          <option value="">Brak</option>
                        </select>
                      </label>
                    </div>

                    <div style={{ marginBottom: spacing.lg }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                          Opis (opcjonalny):
                        </span>
                        <input
                          value={(p as PosRevenuePercent).description ?? ""}
                          placeholder="Tylko wewnętrznie"
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosRevenuePercent), description: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            fontSize: typography.fontSize.base,
                          }}
                        />
                      </label>
                    </div>

                    {/* Period display with missing days check */}
                    <div style={{
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      marginBottom: spacing.md,
                    }}>
                      <div style={{ fontSize: typography.fontSize.sm, marginBottom: spacing.sm }}>
                        <strong>Okres:</strong> {(p as PosRevenuePercent).date_from} — {(p as PosRevenuePercent).date_to}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const shown = !!showMissing[i];
                          if (!shown) silentCheckMissing(i, p as PosRevenuePercent);
                          setShowMissing((prev) => ({ ...prev, [i]: !shown }));
                        }}
                        style={{
                          ...createButtonStyle('secondary'),
                          padding: `${spacing.xs}px ${spacing.md}px`,
                          fontSize: typography.fontSize.sm,
                        }}
                      >
                        {showMissing[i] ? "Ukryj brakujące dni" : "Sprawdź brakujące dni"}
                      </button>
                      {showMissing[i] && (missing[i]?.length ?? 0) > 0 && (
                        <div style={{
                          marginTop: spacing.sm,
                          padding: spacing.md,
                          background: colors.warningLight,
                          border: `1px solid ${colors.warning}`,
                          borderRadius: borderRadius.sm,
                          color: colors.warningDark,
                          fontSize: typography.fontSize.sm
                        }}>
                          <strong>⚠️ Brak danych/konwersji:</strong> {missing[i].join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* BUDGET */}
                {p.type === "budget" && (
                  <div style={{
                    padding: spacing.lg,
                    background: colors.backgroundAlt,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.infoLight,
                      borderLeft: `3px solid ${colors.info}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Budżet</strong>
                      <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Kwota = suma wydanego budżetu (cost) w wybranym okresie (NETTO = BRUTTO / 1.23)
                      </div>
                    </div>

                    <div style={{ marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Źródło
                        </span>
                        <select
                          value={(p as PosBudget).source_type || "google_ads"}
                          onChange={(e) => {
                            const newSourceType = e.target.value as "allegro" | "google_ads";
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosBudget), source_type: newSourceType, customer_id: "", descriptive_name: "" }
                                  : it
                              )
                            );
                            setNickErr((prev) => ({ ...prev, [i]: null }));
                            setNickOptions((prev) => ({ ...prev, [i]: [] }));
                            setMissing((prev) => ({ ...prev, [i]: [] }));
                          }}
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            background: colors.white,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="allegro">Allegro Ads</option>
                          <option value="google_ads">Google Ads</option>
                        </select>
                      </label>
                    </div>

                    <div style={{ marginBottom: spacing.md, position: "relative" }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Klient {(p as PosBudget).source_type === "allegro" ? "Allegro" : "Google Ads"}
                        </span>
                        <input
                          value={(p as PosBudget).descriptive_name}
                          onChange={(e) => handleNickInput(i, e.target.value)}
                          onBlur={() => blurNick(i)}
                          placeholder="min. 2 znaki"
                          autoComplete="off"
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>
                      {!!(nickOptions[i]?.length ?? 0) && (
                        <ul
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: colors.white,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            boxShadow: shadows.md,
                            zIndex: 10,
                            listStyle: "none",
                            margin: 0,
                            marginTop: spacing.xs,
                            padding: 0,
                            maxHeight: 180,
                            overflowY: "auto",
                          }}
                        >
                          {nickOptions[i]!.map((c) => {
                            const sourceType = (p as PosBudget).source_type || "google_ads";
                            const isAllegro = sourceType === "allegro";

                            const displayText = isAllegro
                              ? `${(c as AllegroClient).nickname} (${(c as AllegroClient).id})`
                              : `${(c as GoogleAdsClient).descriptive_name || (c as GoogleAdsClient).customer_id} (${(c as GoogleAdsClient).customer_id})`;

                            const key = isAllegro ? (c as AllegroClient).id : (c as GoogleAdsClient).customer_id;
                            const mccEmail = !isAllegro && (c as GoogleAdsClient).mcc_email ? (c as GoogleAdsClient).mcc_email : null;

                            return (
                              <li
                                key={key}
                                style={{
                                  padding: `${spacing.sm}px ${spacing.md}px`,
                                  cursor: "pointer",
                                  transition: "background-color 0.15s",
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.background = colors.backgroundAlt)}
                                onMouseOut={(e) => (e.currentTarget.style.background = colors.white)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  let customer_id: string, descriptive_name: string;
                                  if (isAllegro) {
                                    const ac = c as AllegroClient;
                                    customer_id = ac.id;
                                    descriptive_name = ac.nickname;
                                  } else {
                                    const gc = c as GoogleAdsClient;
                                    customer_id = gc.customer_id;
                                    descriptive_name = gc.descriptive_name || gc.customer_id;
                                  }
                                  setPositions((arr) =>
                                    arr.map((it, idx) =>
                                      idx === i ? { ...(it as any), customer_id, descriptive_name } : it
                                    )
                                  );
                                  setNickErr((prev) => ({ ...prev, [i]: null }));
                                  setNickOptions((prev) => ({ ...prev, [i]: [] }));
                                }}
                              >
                                {displayText}
                                {mccEmail && <span style={{ color: colors.textSecondary, marginLeft: spacing.sm }}>[MCC: {mccEmail}]</span>}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {nickBusy[i] && <span style={{ marginLeft: spacing.sm, color: colors.textSecondary }}>…</span>}
                      {nickErr[i] && (
                        <div style={{
                          marginTop: spacing.xs,
                          padding: spacing.sm,
                          background: colors.dangerLight,
                          border: `1px solid ${colors.danger}`,
                          borderRadius: borderRadius.sm,
                          color: colors.danger,
                          fontSize: typography.fontSize.sm,
                        }}>
                          {nickErr[i]}
                        </div>
                      )}
                    </div>

                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.border}`,
                    }}>
                      <div style={{ marginBottom: spacing.sm, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                        Polityka daty:
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name={`policy_budget_${i}`}
                          checked={(p as PosBudget).policy === "previous_month"}
                          onChange={() => setPolicyPrev(i)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>Nieokreślony (ostatni pełny miesiąc)</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name={`policy_budget_${i}`}
                          checked={(p as PosBudget).policy === "rolling_30"}
                          onChange={() => setPolicyRolling(i)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>Umowy miesięczne (data od +30 dni)</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name={`policy_budget_${i}`}
                          checked={(p as PosBudget).policy === "custom"}
                          onChange={() => {
                            setPositions((arr) =>
                              arr.map((it, idx) => {
                                if (idx !== i) return it;
                                if (isBudget(it)) {
                                  const bp: PosBudget = {
                                    ...it,
                                    policy: "custom",
                                    anchor_start: null,
                                    month_key: null,
                                    date_from: it.date_from ?? "",
                                    date_to: it.date_to ?? "",
                                  };
                                  return bp;
                                }
                                return it;
                              })
                            );
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: typography.fontSize.sm }}>Custom (wybierz dowolny zakres)</span>
                      </label>

                      {(p as PosBudget).policy === "rolling_30" && (
                        <div style={{ marginTop: spacing.md }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                              Data od:
                            </span>
                            <input
                              type="date"
                              value={(p as PosBudget).anchor_start ?? ""}
                              onChange={(e) => setAnchor(i, e.target.value)}
                              style={{
                                padding: `${spacing.sm}px ${spacing.md}px`,
                                fontSize: typography.fontSize.sm,
                                border: `1px solid ${colors.border}`,
                                borderRadius: borderRadius.sm,
                              }}
                            />
                          </label>
                        </div>
                      )}

                      {(p as PosBudget).policy === "custom" && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md, marginTop: spacing.md }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                              Od:
                            </span>
                            <input
                              type="date"
                              value={(p as PosBudget).date_from ?? ""}
                              onChange={(e) =>
                                setPositions((arr) =>
                                  arr.map((it, idx) =>
                                    idx === i ? { ...(it as PosBudget), date_from: e.target.value } : it
                                  )
                                )
                              }
                              style={{
                                padding: `${spacing.sm}px ${spacing.md}px`,
                                fontSize: typography.fontSize.sm,
                                border: `1px solid ${colors.border}`,
                                borderRadius: borderRadius.sm,
                              }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                              Do:
                            </span>
                            <input
                              type="date"
                              value={(p as PosBudget).date_to ?? ""}
                              onChange={(e) =>
                                setPositions((arr) =>
                                  arr.map((it, idx) =>
                                    idx === i ? { ...(it as PosBudget), date_to: e.target.value } : it
                                  )
                                )
                              }
                              style={{
                                padding: `${spacing.sm}px ${spacing.md}px`,
                                fontSize: typography.fontSize.sm,
                                border: `1px solid ${colors.border}`,
                                borderRadius: borderRadius.sm,
                              }}
                            />
                          </label>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Nazwa pozycji
                        </span>
                        <input
                          value={(p as PosBudget).name ?? ""}
                          placeholder="Budżet Google Ads"
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosBudget), name: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          VAT (%)
                        </span>
                        <input
                          type="number"
                          step="1"
                          value={(p as PosBudget).vat}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosBudget), vat: Number(e.target.value) } : it))
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          GTU
                        </span>
                        <select
                          value={(p as PosBudget).gtu_code || ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosBudget), gtu_code: e.target.value || null } : it))
                            )
                          }
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        >
                          <option value="GTU_12">GTU_12</option>
                          <option value="">Brak</option>
                        </select>
                      </label>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, marginBottom: spacing.md }}>
                      <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                        Opis (opcjonalny)
                      </span>
                      <input
                        value={(p as PosBudget).description ?? ""}
                        onChange={(e) =>
                          setPositions((arr) =>
                            arr.map((it, idx) =>
                              idx === i ? { ...(it as PosBudget), description: e.target.value } : it
                            )
                          )
                        }
                        style={{
                          padding: `${spacing.sm}px ${spacing.md}px`,
                          fontSize: typography.fontSize.sm,
                          border: `1px solid ${colors.border}`,
                          borderRadius: borderRadius.sm,
                        }}
                      />
                    </label>

                    <div style={{
                      padding: spacing.md,
                      background: colors.backgroundAlt,
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.border}`,
                    }}>
                      <div style={{ fontSize: typography.fontSize.sm, marginBottom: spacing.sm }}>
                        <strong>Okres:</strong>{" "}
                        <span style={{ fontWeight: typography.fontWeight.semibold }}>
                          {(p as PosBudget).date_from} — {(p as PosBudget).date_to}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const shown = !!showMissing[i];
                          if (!shown) silentCheckMissing(i, p as PosBudget);
                          setShowMissing((prev) => ({ ...prev, [i]: !shown }));
                        }}
                        style={{
                          ...createButtonStyle('secondary'),
                          padding: `${spacing.xs}px ${spacing.md}px`,
                        }}
                      >
                        {showMissing[i] ? "Ukryj" : "Sprawdź brakujące dni"}
                      </button>
                      {showMissing[i] && (missing[i]?.length ?? 0) > 0 && (
                        <div style={{
                          marginTop: spacing.md,
                          padding: spacing.sm,
                          background: colors.dangerLight,
                          border: `1px solid ${colors.danger}`,
                          borderRadius: borderRadius.sm,
                          color: colors.danger,
                          fontSize: typography.fontSize.sm,
                        }}>
                          <strong>Brak danych/kosztów:</strong> {missing[i].join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ADJUSTMENT */}
                {p.type === "adjustment" && (() => {
                  const adj = p as PosAdj;

                  return (
                  <div style={{
                    padding: spacing.lg,
                    background: colors.backgroundAlt,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.warningLight,
                      borderLeft: `3px solid ${colors.warning}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Wyrównanie</strong>
                      <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Ręczna korekta kwoty (podaj podstawę netto)
                      </div>
                    </div>

                    {/* Wspólne pola: Nazwa + Data od/do */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Nazwa pozycji
                        </span>
                        <input
                          value={adj.name ?? "Wyrównanie"}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdj), name: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Data od
                        </span>
                        <input
                          type="date"
                          value={adj.date_from}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdj), date_from: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Data do
                        </span>
                        <input
                          type="date"
                          value={adj.date_to}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdj), date_to: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>
                    </div>

                    {/* Podstawa netto + VAT + GTU - zawsze widoczne */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Podstawa netto (PLN)
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={adj.podstawa_do_wyrownania ?? 0}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosAdj), podstawa_do_wyrownania: Number(e.target.value) }
                                  : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          VAT (%)
                        </span>
                        <input
                          type="number"
                          step="1"
                          value={adj.vat}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosAdj), vat: Number(e.target.value) } : it))
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          GTU
                        </span>
                        <select
                          value={adj.gtu_code || ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosAdj), gtu_code: e.target.value || null } : it))
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        >
                          <option value="GTU_12">GTU_12</option>
                          <option value="">Brak</option>
                        </select>
                      </label>
                    </div>

                    {/* Opis */}
                    <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, marginBottom: spacing.md }}>
                      <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                        Opis (opcjonalny - tylko wewnętrznie)
                      </span>
                      <input
                        value={adj.description ?? ""}
                        onChange={(e) =>
                          setPositions((arr) =>
                            arr.map((it, idx) =>
                              idx === i ? { ...(it as PosAdj), description: e.target.value } : it
                            )
                          )
                        }
                        style={{
                          padding: spacing.sm,
                          fontSize: typography.fontSize.sm,
                          border: `1px solid ${colors.border}`,
                          borderRadius: borderRadius.sm,
                        }}
                      />
                    </label>
                  </div>
                  );
                })()}

                {/* ADJUSTMENT PERCENT */}
                {p.type === "adjustment_percent" && (() => {
                  const ap = p as PosAdjPercent;
                  const apSourceType = ap.source_type || "allegro";

                  return (
                  <div style={{
                    padding: spacing.lg,
                    background: colors.backgroundAlt,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border}`,
                  }}>
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.warningLight,
                      borderLeft: `3px solid ${colors.warning}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Wyrównanie %</strong>
                      <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Wyrównanie obliczone jako procent od budżetu klienta
                      </div>
                    </div>

                    {/* Wspólne pola: Nazwa + Data od/do */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Nazwa pozycji
                        </span>
                        <input
                          value={ap.name ?? "Wyrównanie %"}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdjPercent), name: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Data od
                        </span>
                        <input
                          type="date"
                          value={ap.date_from}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdjPercent), date_from: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Data do
                        </span>
                        <input
                          type="date"
                          value={ap.date_to}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdjPercent), date_to: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>
                    </div>

                    {/* VAT + GTU + Opis */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          VAT (%)
                        </span>
                        <input
                          type="number"
                          step="1"
                          value={ap.vat}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosAdjPercent), vat: Number(e.target.value) } : it))
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          GTU
                        </span>
                        <select
                          value={ap.gtu_code || ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) => (idx === i ? { ...(it as PosAdjPercent), gtu_code: e.target.value || null } : it))
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        >
                          <option value="GTU_12">GTU_12</option>
                          <option value="">Brak</option>
                        </select>
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Opis (opcjonalny - tylko wewnętrznie)
                        </span>
                        <input
                          value={ap.description ?? ""}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i ? { ...(it as PosAdjPercent), description: e.target.value } : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>
                    </div>

                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.primaryLight,
                      borderLeft: `3px solid ${colors.primary}`,
                      borderRadius: borderRadius.sm,
                    }}>
                      <strong style={{ fontSize: typography.fontSize.sm }}>Pola procentowe</strong>
                      <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                        Kwota wyrównania = (budżet klienta - próg) × procent / 100
                      </div>
                    </div>

                    {/* Source Selector */}
                    <div style={{ marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Źródło danych:
                        </span>
                        <select
                          value={apSourceType}
                          onChange={(e) => {
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? {
                                      ...(it as PosAdjPercent),
                                      source_type: e.target.value as "allegro" | "google_ads",
                                      client_id: "",
                                      client_numeric_id: "",
                                    }
                                  : it
                              )
                            );
                            setNickOptions((prev) => ({ ...prev, [i]: [] }));
                            setNickErr((prev) => ({ ...prev, [i]: null }));
                            setMissing((prev) => ({ ...prev, [i]: [] }));
                          }}
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        >
                          <option value="allegro">Allegro Ads</option>
                          <option value="google_ads">Google Ads</option>
                        </select>
                      </label>
                    </div>

                    {/* Client Search */}
                    <div style={{ position: "relative", marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Klient {apSourceType === 'allegro' ? 'Allegro' : 'Google Ads'}:
                        </span>
                        <input
                          value={apSourceType === 'google_ads' && ap.client_display_name ? ap.client_display_name : (ap.client_id || "")}
                          onChange={async (e) => {
                            const value = e.target.value;
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosAdjPercent), client_id: value, client_numeric_id: "", client_display_name: "" }
                                  : it
                              )
                            );

                            if (value.length < 2) {
                              setNickOptions((prev) => ({ ...prev, [i]: [] }));
                              return;
                            }

                            // Debounced search
                            if (debounceRef.current[i]) clearTimeout(debounceRef.current[i]);
                            debounceRef.current[i] = window.setTimeout(async () => {
                              try {
                                setNickBusy((prev) => ({ ...prev, [i]: true }));
                                let resp;
                                if (apSourceType === 'allegro') {
                                  resp = await api.get("/allegro-clients", { params: { nickname: value } });
                                } else {
                                  resp = await api.get("/google-ads/customers", { params: { search: value } });
                                }
                                setNickOptions((prev) => ({ ...prev, [i]: Array.isArray(resp.data) ? resp.data : [] }));
                              } catch {
                                setNickOptions((prev) => ({ ...prev, [i]: [] }));
                              } finally {
                                setNickBusy((prev) => ({ ...prev, [i]: false }));
                              }
                            }, 300);
                          }}
                          placeholder={apSourceType === 'allegro' ? "Wpisz nick (min 2 znaki)" : "Wpisz nazwę klienta (min 2 znaki)"}
                          autoComplete="off"
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                            width: "100%",
                          }}
                        />
                      </label>
                      {nickBusy[i] && (
                        <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                          Szukam...
                        </div>
                      )}
                      {(nickOptions[i]?.length ?? 0) > 0 && (
                        <ul style={{
                          position: "absolute", top: "100%", left: 0, background: colors.background,
                          border: `1px solid ${colors.border}`, zIndex: 1000, listStyle: "none",
                          margin: `${spacing.xs}px 0 0 0`, padding: 0, width: "100%", maxHeight: 180, overflowY: "auto",
                          boxShadow: shadows.lg, borderRadius: borderRadius.sm
                        }}>
                          {nickOptions[i].map((c: Client) => {
                            const isAllegro = apSourceType === 'allegro';
                            const displayText = isAllegro
                              ? `${(c as AllegroClient).nickname} (${c.id})`
                              : `${(c as GoogleAdsClient).descriptive_name || (c as GoogleAdsClient).customer_id} (${(c as GoogleAdsClient).customer_id})`;

                            return (
                              <li key={isAllegro ? c.id : (c as GoogleAdsClient).customer_id}
                                  style={{
                                    padding: spacing.md,
                                    cursor: "pointer",
                                    borderBottom: `1px solid ${colors.borderLight}`,
                                    transition: "background 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                  onClick={() => {
                                    if (isAllegro) {
                                      const ac = c as AllegroClient;
                                      setPositions((arr) =>
                                        arr.map((it, idx) =>
                                          idx === i
                                            ? {
                                                ...(it as PosAdjPercent),
                                                client_id: ac.nickname,
                                                client_numeric_id: String(ac.id),
                                              }
                                            : it
                                        )
                                      );
                                    } else {
                                      const gc = c as GoogleAdsClient;
                                      setPositions((arr) =>
                                        arr.map((it, idx) =>
                                          idx === i
                                            ? {
                                                ...(it as PosAdjPercent),
                                                client_id: gc.customer_id,
                                                client_numeric_id: "",
                                                client_display_name: gc.descriptive_name || gc.customer_id,
                                              }
                                            : it
                                        )
                                      );
                                    }
                                    setNickOptions((prev) => ({ ...prev, [i]: [] }));
                                    setNickErr((prev) => ({ ...prev, [i]: null }));
                                  }}>
                                {displayText}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    {/* Procent + Próg */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Procent (%):
                        </span>
                        <input
                          type="number"
                          step="0.1"
                          value={ap.percent ?? 10}
                          min={0}
                          max={100}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosAdjPercent), percent: Number(e.target.value) }
                                  : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>

                      <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Próg (PLN):
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={ap.threshold ?? 1000}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosAdjPercent), threshold: Number(e.target.value) }
                                  : it
                              )
                            )
                          }
                          style={{
                            padding: spacing.sm,
                            fontSize: typography.fontSize.sm,
                            border: `1px solid ${colors.border}`,
                            borderRadius: borderRadius.sm,
                          }}
                        />
                      </label>
                    </div>

                    {/* Cap (limit nadwyżki) */}
                    <div style={{
                      marginBottom: spacing.md,
                      padding: spacing.md,
                      background: colors.gray50,
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.border}`
                    }}>
                      <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer", marginBottom: ap.cap_enabled ? spacing.md : 0 }}>
                        <input
                          type="checkbox"
                          checked={ap.cap_enabled ?? false}
                          onChange={(e) =>
                            setPositions((arr) =>
                              arr.map((it, idx) =>
                                idx === i
                                  ? { ...(it as PosAdjPercent), cap_enabled: e.target.checked }
                                  : it
                              )
                            )
                          }
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                          Blokada nadwyżki (cap)
                        </span>
                      </label>
                      {ap.cap_enabled && (
                        <div style={{ marginTop: spacing.sm }}>
                          <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                              Maksymalna kwota netto (PLN):
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min={0}
                              value={ap.cap_amount ?? 0}
                              onChange={(e) =>
                                setPositions((arr) =>
                                  arr.map((it, idx) =>
                                    idx === i
                                      ? { ...(it as PosAdjPercent), cap_amount: Number(e.target.value) }
                                      : it
                                  )
                                )
                              }
                              placeholder="Wpisz maksymalną kwotę"
                              style={{
                                padding: spacing.sm,
                                fontSize: typography.fontSize.sm,
                                border: `1px solid ${colors.border}`,
                                borderRadius: borderRadius.sm,
                              }}
                            />
                          </label>
                          <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                            Jeśli obliczona kwota przekroczy limit, zostanie obcięta do tej wartości.
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Missing days check */}
                    <div style={{
                      padding: spacing.md,
                      background: colors.backgroundAlt,
                      borderRadius: borderRadius.sm,
                      border: `1px solid ${colors.border}`,
                    }}>
                      <div style={{ fontSize: typography.fontSize.sm, marginBottom: spacing.sm }}>
                        <strong>Okres:</strong>{" "}
                        <span style={{ fontWeight: typography.fontWeight.semibold }}>
                          {ap.date_from} — {ap.date_to}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const shown = !!showMissing[i];
                          if (!shown && ap.client_id && ap.date_from && ap.date_to) {
                            // Fetch missing days
                            try {
                              let resp;
                              if (apSourceType === 'allegro') {
                                const numericId = ap.client_numeric_id;
                                if (!numericId) {
                                  setNickErr((prev) => ({ ...prev, [i]: "Wybierz klienta z listy." }));
                                  return;
                                }
                                resp = await api.get("/missing-days", {
                                  params: { client_id: numericId, date_from: ap.date_from, date_to: ap.date_to }
                                });
                              } else {
                                resp = await api.get("/google-ads/missing-days", {
                                  params: { customer_id: ap.client_id, date_from: ap.date_from, date_to: ap.date_to }
                                });
                              }
                              const payload = resp.data;
                              const days = Array.isArray(payload) ? payload : (payload.missing ?? []);
                              setMissing((prev) => ({ ...prev, [i]: days }));
                            } catch (err: any) {
                              console.error("Missing days check error:", err);
                              setNickErr((prev) => ({ ...prev, [i]: `Błąd: ${err?.response?.data?.detail || err?.message}` }));
                            }
                          }
                          setShowMissing((prev) => ({ ...prev, [i]: !shown }));
                        }}
                        style={{
                          ...createButtonStyle('secondary'),
                          padding: `${spacing.xs}px ${spacing.md}px`,
                        }}
                      >
                        {showMissing[i] ? "Ukryj" : "Sprawdź brakujące dni"}
                      </button>
                      {showMissing[i] && (missing[i]?.length ?? 0) > 0 && (
                        <div style={{
                          marginTop: spacing.md,
                          padding: spacing.sm,
                          background: colors.dangerLight,
                          border: `1px solid ${colors.danger}`,
                          borderRadius: borderRadius.sm,
                          color: colors.danger,
                          fontSize: typography.fontSize.sm,
                        }}>
                          <strong>Brak danych/kosztów:</strong> {missing[i].join(", ")}
                        </div>
                      )}
                      {showMissing[i] && (missing[i]?.length ?? 0) === 0 && (
                        <div style={{
                          marginTop: spacing.md,
                          padding: spacing.sm,
                          background: colors.successLight,
                          border: `1px solid ${colors.success}`,
                          borderRadius: borderRadius.sm,
                          color: colors.success,
                          fontSize: typography.fontSize.sm,
                        }}>
                          <strong>✓ Wszystkie dni mają dane</strong>
                        </div>
                      )}
                      {nickErr[i] && (
                        <div style={{
                          marginTop: spacing.md,
                          padding: spacing.sm,
                          background: colors.dangerLight,
                          border: `1px solid ${colors.danger}`,
                          borderRadius: borderRadius.sm,
                          color: colors.danger,
                          fontSize: typography.fontSize.sm,
                        }}>
                          {nickErr[i]}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })()}
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                <button
                  onClick={() => removeAt(i)}
                  style={{
                    ...createButtonStyle('danger'),
                    padding: `${spacing.xs}px ${spacing.md}px`,
                    fontSize: typography.fontSize.sm,
                  }}
                >
                  🗑️ Usuń
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Bitrix Deals Section - zawsze widoczna */}
      <div style={{
        marginTop: spacing.xxl,
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: spacing.lg,
        }}>
          <h3 style={{
            margin: 0,
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
          }}>
            Konta Ads - Bitrix
          </h3>
          <button
            onClick={() => setShowAddAccount(!showAddAccount)}
            style={{
              ...createButtonStyle("primary"),
              padding: `${spacing.xs}px ${spacing.md}px`,
              fontSize: typography.fontSize.sm,
            }}
          >
            {showAddAccount ? "Anuluj" : "+ Dodaj konto"}
          </button>
        </div>

        {showAddAccount && (
          <div style={{
            padding: spacing.md,
            background: colors.background,
            borderRadius: borderRadius.sm,
            border: `1px solid ${colors.border}`,
            marginBottom: spacing.md,
          }}>
            <div style={{ display: "flex", gap: spacing.md, alignItems: "center" }}>
              <select
                value={newAccountType}
                onChange={(e) => {
                  setNewAccountType(e.target.value as "allegro" | "google_ads");
                  setAddAccountSearchQuery("");
                  setAddAccountOptions([]);
                  setNewAccountId("");
                  setNewAccountDisplayName("");
                }}
                style={{
                  padding: `${spacing.sm}px ${spacing.md}px`,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.sm,
                  fontSize: typography.fontSize.sm,
                  minWidth: 130,
                  height: 40,
                }}
              >
                <option value="allegro">Allegro</option>
                <option value="google_ads">Google Ads</option>
              </select>
              <div style={{ position: "relative", width: 300 }}>
                <input
                  type="text"
                  value={addAccountSearchQuery}
                  onChange={(e) => {
                    handleAddAccountSearch(e.target.value);
                    if (newAccountId && e.target.value !== newAccountDisplayName) {
                      setNewAccountId("");
                      setNewAccountDisplayName("");
                    }
                  }}
                  placeholder="Wyszukaj konto..."
                  style={{
                    width: "100%",
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    border: `1px solid ${newAccountId ? colors.success : colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: typography.fontSize.sm,
                    background: newAccountId ? colors.successLight : colors.background,
                    height: 40,
                    boxSizing: "border-box",
                  }}
                />
                {addAccountSearchBusy && (
                  <span style={{
                    position: "absolute",
                    right: spacing.sm,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: typography.fontSize.sm,
                    color: colors.textSecondary,
                  }}>
                    ⏳
                  </span>
                )}
                {addAccountOptions.length > 0 && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    boxShadow: shadows.md,
                    zIndex: 100,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}>
                    {addAccountOptions.map((c) => {
                      const isGoogle = newAccountType === "google_ads";
                      const gc = c as GoogleAdsClient;
                      const ac = c as AllegroClient;
                      const name = isGoogle ? (gc.descriptive_name || gc.customer_id) : ac.nickname;
                      const id = isGoogle ? gc.customer_id : ac.id;
                      return (
                        <div
                          key={id}
                          onClick={() => handleSelectAddAccount(c)}
                          style={{
                            padding: `${spacing.sm}px ${spacing.md}px`,
                            cursor: "pointer",
                            borderBottom: `1px solid ${colors.border}`,
                            fontSize: typography.fontSize.sm,
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = colors.gray100}
                          onMouseLeave={(e) => e.currentTarget.style.background = colors.background}
                        >
                          <div style={{ fontWeight: typography.fontWeight.medium }}>{name}</div>
                          <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                            ID: {id}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {newAccountId && (
                <span style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.success,
                  whiteSpace: "nowrap",
                }}>
                  ✓ {newAccountDisplayName}
                </span>
              )}
              <button
                onClick={handleAddAccount}
                disabled={addingAccount || !newAccountId.trim()}
                style={{
                  ...createButtonStyle("success"),
                  padding: `${spacing.sm}px ${spacing.lg}px`,
                  fontSize: typography.fontSize.sm,
                  height: 40,
                  minWidth: 90,
                  opacity: addingAccount || !newAccountId.trim() ? 0.5 : 1,
                }}
              >
                {addingAccount ? "..." : "Dodaj"}
              </button>
            </div>
            {addAccountError && (
              <div style={{
                marginTop: spacing.sm,
                padding: spacing.sm,
                background: colors.errorLight,
                color: colors.errorDark,
                borderRadius: borderRadius.sm,
                fontSize: typography.fontSize.sm,
              }}>
                {addAccountError}
              </div>
            )}
          </div>
        )}

          {loadingDeals && (
            <div style={{
              padding: spacing.md,
              color: colors.textSecondary,
              textAlign: "center",
            }}>
              Ladowanie informacji o dealach...
            </div>
          )}

          {dealsError && (
            <div style={{
              padding: spacing.md,
              background: colors.errorLight,
              color: colors.errorDark,
              borderRadius: borderRadius.sm,
              marginBottom: spacing.md,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <span>{dealsError}</span>
              <button
                onClick={fetchBitrixDeals}
                style={{
                  ...createButtonStyle("secondary"),
                  padding: `${spacing.xs}px ${spacing.sm}px`,
                  fontSize: typography.fontSize.xs,
                }}
              >
                Ponow
              </button>
            </div>
          )}

          {!loadingDeals && (
            <div style={{ overflow: "visible" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              background: colors.background,
              borderRadius: borderRadius.sm,
            }}>
              <thead>
                <tr>
                  <th style={{
                    ...th,
                    background: colors.gray100,
                    color: colors.textPrimary,
                  }}>Konto</th>
                  <th style={{
                    ...th,
                    background: colors.gray100,
                    color: colors.textPrimary,
                  }}>Deal Bitrix</th>
                  <th style={{
                    ...th,
                    background: colors.gray100,
                    color: colors.textPrimary,
                  }}>Etap</th>
                  <th style={{
                    ...th,
                    background: colors.gray100,
                    color: colors.textPrimary,
                  }}>Budzet</th>
                  <th style={{
                    ...th,
                    background: colors.gray100,
                    color: colors.textPrimary,
                  }}>Notatka budzetu</th>
                  <th style={{
                    ...th,
                    background: colors.gray100,
                    color: colors.textPrimary,
                    minWidth: 320,
                  }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {accountDeals.map((account) => {
                  const isEditing =
                    editingAccount?.sourceType === account.source_type &&
                    editingAccount?.clientId === account.client_id;
                  const hasDeal = !!account.bitrix_deal;

                  return (
                    <tr key={`${account.source_type}-${account.client_id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: typography.fontWeight.medium }}>
                          {account.display_name}
                        </div>
                        <div style={{
                          fontSize: typography.fontSize.xs,
                          color: colors.textSecondary,
                        }}>
                          {account.source_type === "allegro" ? "Allegro" : "Google Ads"}
                        </div>
                      </td>
                      <td style={td}>
                        {hasDeal ? account.bitrix_deal!.title : <span style={{ color: colors.textSecondary }}>Nie przypisano</span>}
                      </td>
                      <td style={td}>
                        {hasDeal ? account.bitrix_deal!.stage_name : "-"}
                      </td>
                      <td style={td}>
                        {hasDeal && account.bitrix_deal!.budget !== null
                          ? account.bitrix_deal!.budget.toLocaleString("pl-PL", {
                              style: "currency",
                              currency: "PLN",
                            })
                          : "-"}
                      </td>
                      <td style={td}>
                        {hasDeal && account.bitrix_deal!.budget_note
                          ? account.bitrix_deal!.budget_note
                          : "-"}
                      </td>
                      <td style={{ ...td, position: "relative", zIndex: isEditing ? 1000 : "auto" }}>
                        {isEditing ? (
                          <div style={{ width: "100%", position: "relative" }}>
                            <BitrixDealSearch
                              currentDeal={account.bitrix_deal}
                              onSelect={(deal) =>
                                handleAssignDeal(account.source_type, account.client_id, deal)
                              }
                              onClear={() =>
                                handleClearDeal(account.source_type, account.client_id)
                              }
                            />
                            <button
                              onClick={() => setEditingAccount(null)}
                              style={{
                                marginTop: spacing.xs,
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                color: colors.textSecondary,
                                fontSize: typography.fontSize.xs,
                              }}
                            >
                              Anuluj
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: spacing.xs }}>
                            <button
                              onClick={() =>
                                setEditingAccount({
                                  sourceType: account.source_type,
                                  clientId: account.client_id,
                                })
                              }
                              style={{
                                ...createButtonStyle(hasDeal ? "secondary" : "primary"),
                                padding: `${spacing.xs}px ${spacing.md}px`,
                                fontSize: typography.fontSize.sm,
                              }}
                            >
                              {hasDeal ? "Zmień" : "Przypisz"}
                            </button>
                            {hasDeal && (
                              <button
                                onClick={() =>
                                  handleRemoveDeal(account.source_type, account.client_id)
                                }
                                style={{
                                  ...createButtonStyle("secondary"),
                                  padding: `${spacing.xs}px ${spacing.md}px`,
                                  fontSize: typography.fontSize.sm,
                                  color: colors.errorDark,
                                  borderColor: colors.errorDark,
                                }}
                              >
                                Usuń przypisanie
                              </button>
                            )}
                            {!isAccountFromPosition(account.source_type, account.client_id, account.display_name) && (
                              <button
                                onClick={() =>
                                  handleRemoveAccount(account.source_type, account.client_id)
                                }
                                title="Usuń konto z draftu (dodane ręcznie)"
                                style={{
                                  ...createButtonStyle("secondary"),
                                  padding: `${spacing.xs}px ${spacing.md}px`,
                                  fontSize: typography.fontSize.sm,
                                  color: colors.error,
                                  borderColor: colors.error,
                                  background: colors.errorLight,
                                }}
                              >
                                🗑️ Usuń konto
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}

          {accountDeals.length === 0 && !loadingDeals && !dealsError && (
            <div style={{
              padding: spacing.md,
              color: colors.textSecondary,
              textAlign: "center",
            }}>
              Brak kont Ads. Użyj przycisku "Dodaj konto" aby dodać ręcznie.
            </div>
          )}
        </div>

      <div style={{
        display: "flex",
        gap: spacing.lg,
        marginTop: spacing.xxl,
        justifyContent: "center",
      }}>
        <button
          onClick={save}
          style={{
            ...createButtonStyle('success'),
            fontSize: typography.fontSize.lg,
            padding: `${spacing.md}px ${spacing.xxl}px`,
          }}
        >
          ✅ Zapisz draft
        </button>
        <button
          onClick={() => window.history.back()}
          style={{
            ...createButtonStyle('secondary'),
            fontSize: typography.fontSize.base,
          }}
        >
          ← Anuluj
        </button>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  padding: spacing.md,
  fontWeight: typography.fontWeight.semibold,
  background: colors.primary,
  color: "white",
  fontSize: typography.fontSize.sm,
};
const td: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  padding: spacing.md,
  verticalAlign: "top",
};
